import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const HashtagAnalytics = ({ title = "Hashtag Performance" }) => {
  const [timeframe, setTimeframe] = useState('week');

  const hashtagData = [
    {
      hashtag: '#DruTravels',
      mentions: 1247,
      reach: 45620,
      engagement: 8.9,
      sentiment: 'positive',
      growth: 12.5,
      color: 'var(--color-primary)'
    },
    {
      hashtag: '#TamilNaduTourism',
      mentions: 892,
      reach: 32450,
      engagement: 7.8,
      sentiment: 'positive',
      growth: 8.3,
      color: 'var(--color-secondary)'
    },
    {
      hashtag: '#SouthIndia',
      mentions: 756,
      reach: 28930,
      engagement: 6.9,
      sentiment: 'positive',
      growth: 15.2,
      color: 'var(--color-accent)'
    },
    {
      hashtag: '#HillStation',
      mentions: 634,
      reach: 24580,
      engagement: 9.2,
      sentiment: 'positive',
      growth: 22.1,
      color: 'var(--color-success)'
    },
    {
      hashtag: '#KeralaBackwaters',
      mentions: 523,
      reach: 19870,
      engagement: 8.5,
      sentiment: 'positive',
      growth: 6.7,
      color: 'var(--color-warning)'
    },
    {
      hashtag: '#Heritage',
      mentions: 445,
      reach: 16720,
      engagement: 7.3,
      sentiment: 'neutral',
      growth: -2.1,
      color: '#8B5CF6'
    }
  ];

  const trendingHashtags = [
    { tag: '#OotyTourism', growth: 45.2, mentions: 234 },
    { tag: '#WeekendGetaway', growth: 38.7, mentions: 189 },
    { tag: '#SoloTravel', growth: 32.1, mentions: 156 },
    { tag: '#CulturalTour', growth: 28.9, mentions: 134 },
    { tag: '#CoastalDrive', growth: 25.4, mentions: 112 }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-medium">
          <p className="font-medium text-foreground mb-2">{data.hashtag}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mentions:</span>
              <span className="font-medium text-foreground">{data.mentions.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Reach:</span>
              <span className="font-medium text-foreground">{data.reach.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Engagement:</span>
              <span className="font-medium text-foreground">{data.engagement}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'positive') return 'text-success';
    if (sentiment === 'negative') return 'text-error';
    return 'text-warning';
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === 'positive') return 'TrendingUp';
    if (sentiment === 'negative') return 'TrendingDown';
    return 'Minus';
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
          className="text-sm border border-border rounded-md px-3 py-1 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="day">Last 24 hours</option>
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hashtag Distribution Chart */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-4">Mention Distribution</h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={hashtagData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="mentions"
                >
                  {hashtagData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Hashtags List */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-4">Top Performing Hashtags</h4>
          <div className="space-y-3">
            {hashtagData.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-micro">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="font-medium text-foreground text-sm">{item.hashtag}</span>
                  </div>
                  <Icon 
                    name={getSentimentIcon(item.sentiment)} 
                    size={14} 
                    className={getSentimentColor(item.sentiment)}
                  />
                </div>
                
                <div className="flex items-center space-x-4 text-sm">
                  <div className="text-center">
                    <div className="font-semibold text-foreground">{formatNumber(item.mentions)}</div>
                    <div className="text-muted-foreground text-xs">Mentions</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold text-foreground">{item.engagement}%</div>
                    <div className="text-muted-foreground text-xs">Engagement</div>
                  </div>
                  <div className={`text-center ${item.growth >= 0 ? 'text-success' : 'text-error'}`}>
                    <div className="font-semibold flex items-center">
                      <Icon 
                        name={item.growth >= 0 ? 'ArrowUp' : 'ArrowDown'} 
                        size={12} 
                        className="mr-1"
                      />
                      {Math.abs(item.growth)}%
                    </div>
                    <div className="text-muted-foreground text-xs">Growth</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trending Hashtags */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="text-sm font-medium text-muted-foreground mb-4">Trending Hashtags</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {trendingHashtags.map((item, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3 text-center">
              <div className="font-medium text-foreground text-sm mb-1">{item.tag}</div>
              <div className="flex items-center justify-center space-x-1 text-success text-xs">
                <Icon name="TrendingUp" size={12} />
                <span>{item.growth}%</span>
              </div>
              <div className="text-muted-foreground text-xs mt-1">
                {item.mentions} mentions
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HashtagAnalytics;